/**
 * FP3-119B — Shared React Query hooks for Admin datasets.
 *
 * Every admin page dataset gets a `useAdminXxx()` hook here so the
 * cache-key, staleTime, and invalidation contract are declared ONCE
 * and reused everywhere. Any mutation elsewhere that changes the
 * underlying data must call `invalidateAdminXxx()` (also declared
 * here) so the affected caches refresh — but nothing else does.
 */
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { queryClient, STALE, authScopeKey } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";

// ── /admin/providers ─────────────────────────────────────────────
export function adminProvidersKey(scope, params) {
  return ["admin", "providers", scope, params || {}];
}
export function useAdminProviders(params) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: adminProvidersKey(scope, params),
    queryFn: async () => (await api.getAdminProviders(params)).data,
    staleTime: STALE.PROVIDERS,
  });
}
export function invalidateAdminProviders() {
  queryClient.invalidateQueries({ queryKey: ["admin", "providers"] });
}

// ── /admin/students ─────────────────────────────────────────────
export function adminStudentsKey(scope, params) {
  return ["admin", "students", scope, params || {}];
}
export function useAdminStudents(params, options = {}) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: adminStudentsKey(scope, params),
    queryFn: async () => (await api.getAdminStudents(params)).data,
    staleTime: STALE.STUDENTS_DIRECTORY,
    ...options,
  });
}
export function invalidateAdminStudents() {
  queryClient.invalidateQueries({ queryKey: ["admin", "students"] });
}

// ── /admin/students/summary ────────────────────────────────────
export function adminStudentsSummaryKey(scope, params) {
  return ["admin", "students", "summary", scope, params || {}];
}
export function useAdminStudentsSummary(params, options = {}) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: adminStudentsSummaryKey(scope, params),
    queryFn: async () => (await api.getAdminStudentsSummary(params)).data,
    staleTime: STALE.STUDENTS_DIRECTORY,
    ...options,
  });
}
export function invalidateAdminStudentsSummary() {
  queryClient.invalidateQueries({ queryKey: ["admin", "students", "summary"] });
}

// ── /progress/admin ─────────────────────────────────────────────
export function adminProgressKey(scope, params) {
  return ["admin", "progress", scope, params || {}];
}
export function useAdminProgress(params) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: adminProgressKey(scope, params),
    queryFn: async () => (await api.getAdminProgress(params)).data,
    staleTime: STALE.PROGRESS,
  });
}
export function invalidateAdminProgress() {
  queryClient.invalidateQueries({ queryKey: ["admin", "progress"] });
}

// ── /reports (summary + trends) ────────────────────────────────
export function reportsKey(scope, endpoint, params) {
  return ["reports", endpoint, scope, params || {}];
}
export function useReports(endpoint, params, options = {}) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: reportsKey(scope, endpoint, params),
    queryFn: async () => {
      const fn = api[endpoint];
      if (typeof fn !== "function") throw new Error(`api.${endpoint} missing`);
      return (await fn(params)).data;
    },
    staleTime: STALE.REPORTS,
    ...options,
  });
}
export function invalidateReports() {
  queryClient.invalidateQueries({ queryKey: ["reports"] });
}

// ── /admin/coverage-risks ──────────────────────────────────────
export function adminCoverageKey(scope, params) {
  return ["admin", "coverage", scope, params || {}];
}
export function useAdminCoverage(params) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: adminCoverageKey(scope, params),
    queryFn: async () => (await api.getAdminCoverageRisks(params)).data,
    staleTime: STALE.COVERAGE,
  });
}
export function invalidateAdminCoverage() {
  queryClient.invalidateQueries({ queryKey: ["admin", "coverage"] });
}

// ── /due-dates ─────────────────────────────────────────────────
export function dueDatesKey(scope, params) {
  return ["due-dates", scope, params || {}];
}
export function useDueDates(params) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: dueDatesKey(scope, params),
    queryFn: async () => (await api.getDueDates(params)).data,
    staleTime: STALE.DUE_DATES,
  });
}
export function invalidateDueDates() {
  queryClient.invalidateQueries({ queryKey: ["due-dates"] });
}

// ── /schools ───────────────────────────────────────────────────
export function schoolsKey(scope, params) {
  return ["schools", scope, params || {}];
}
export function useSchools(params) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: schoolsKey(scope, params),
    queryFn: async () => (await api.listSchools(params)).data,
    staleTime: STALE.SCHOOLS,
  });
}
export function invalidateSchools() {
  queryClient.invalidateQueries({ queryKey: ["schools"] });
}

// ── /admin/schools (richer admin roster with billing / access) ─
export function adminSchoolsKey(scope, params) {
  return ["admin", "schools", scope, params || {}];
}
export function useAdminSchools(params, options = {}) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: adminSchoolsKey(scope, params),
    queryFn: async () => (await api.listAdminSchools(params)).data,
    staleTime: STALE.SCHOOLS,
    ...options,
  });
}
export function invalidateAdminSchools() {
  queryClient.invalidateQueries({ queryKey: ["admin", "schools"] });
}

// ── /admin/student-assignments ─────────────────────────────────
export function studentAssignmentsKey(scope, params) {
  return ["admin", "student-assignments", scope, params || {}];
}
export function useStudentAssignments(params) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: studentAssignmentsKey(scope, params),
    queryFn: async () => (await api.getAdminStudentAssignments(params)).data,
    staleTime: STALE.ASSIGNMENTS,
  });
}
export function invalidateStudentAssignments() {
  queryClient.invalidateQueries({ queryKey: ["admin", "student-assignments"] });
}

// ── /admin/dashboard ────────────────────────────────────────────
// FP3-120 — Same steady-state contract as the other admin surfaces.
// Cache key includes `as_org` / `as_school` (from URL) so a super
// admin switching context gets a fresh slot without leaking rows.
export function adminDashboardKey(scope, params) {
  return ["admin", "dashboard", scope, params || {}];
}
export function useAdminDashboard(params, options = {}) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: adminDashboardKey(scope, params),
    queryFn: async () => (await api.getAdminDashboard(params)).data,
    staleTime: STALE.DASHBOARD,
    ...options,
  });
}
export function invalidateAdminDashboard() {
  queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
}

// ── /schedule/team + /schedule/conflicts (calendar week bundle) ─
// FP3-120 — Weekly team schedule + conflicts fetched together so a
// week's calendar renders atomically from a single cache slot. Key
// includes start/end date so week navigation opens a separate slot
// (and revisits are cache hits). Provider filter is client-side so
// it stays out of the key.
export function scheduleTeamKey(scope, params) {
  return ["schedule", "team", scope, params || {}];
}
export function useScheduleTeamWeek(params, options = {}) {
  const auth = useAuth();
  const scope = authScopeKey(auth);
  return useQuery({
    queryKey: scheduleTeamKey(scope, params),
    queryFn: async () => {
      const teamParams = { start_date: params.start_date, end_date: params.end_date };
      if (params.as_org) teamParams.as_org = params.as_org;
      if (params.as_school) teamParams.as_school = params.as_school;
      const conflictsParams = {
        start_date: params.start_date,
        end_date: params.end_date,
      };
      const [team, confl] = await Promise.all([
        api.getScheduleTeam(teamParams),
        api.getScheduleConflicts(conflictsParams),
      ]);
      return {
        providers: team.data?.providers || [],
        sessions: team.data?.sessions || [],
        conflicts: confl.data?.shared_conflicts || confl.data?.conflicts || [],
      };
    },
    staleTime: STALE.CALENDAR,
    ...options,
  });
}
export function invalidateScheduleTeam() {
  queryClient.invalidateQueries({ queryKey: ["schedule", "team"] });
}
/**
 * Called from mutation success handlers. Each mutation type maps to
 * a bounded list of caches it can affect. Never call
 * `queryClient.clear()` from a mutation — that would defeat the
 * whole steady-state model.
 */
export const invalidateAfterMutation = {
  studentEdited: () => {
    invalidateAdminStudents();
    invalidateAdminStudentsSummary();
    invalidateStudentAssignments();
    invalidateAdminProgress();
    invalidateReports();
    // FP3-120 — Dashboard student/campus counts.
    invalidateAdminDashboard();
    // FP3-122B — Provider Calendar + Admin Shared Calendar bundles
    // carry the student label per session; a rename must refresh
    // them so the corrected name is visible without a hard refresh.
    // Coverage / Risk widgets also render student names on their
    // hotspot chips.
    invalidateScheduleTeam();
    invalidateAdminCoverage();
  },
  providerAssignmentChanged: () => {
    invalidateAdminStudents();
    invalidateAdminStudentsSummary();
    invalidateAdminProviders();
    invalidateStudentAssignments();
    invalidateReports();
    // FP3-120 — Dashboard provider counts + unavailable-today list.
    invalidateAdminDashboard();
  },
  sessionLogged: () => {
    invalidateAdminProgress();
    invalidateReports();
    invalidateAdminCoverage();
    // FP3-120 — Today counts on the dashboard.
    invalidateAdminDashboard();
    // FP3-125 — Session write can change goal freshness (stale-goal
    // flag) + tracker counts on both provider Today and Student
    // Workspace. Student payload isn't React Query cached so it
    // refreshes on next fetch; goal_evidence / trial_run listings
    // (used by admin drill-ins) ARE cached — bust them.
    queryClient.invalidateQueries({ queryKey: ["goal-evidence"] });
    queryClient.invalidateQueries({ queryKey: ["trial-runs"] });
  },
  scheduleChanged: () => {
    // FP3-120 — Any recurring-block / one-off session write affects
    // the calendar bundle AND the dashboard Today-sessions card.
    invalidateScheduleTeam();
    invalidateAdminDashboard();
    invalidateAdminCoverage();
  },
  providerHealthChanged: () => {
    // FP3-120 — Coverage & Risk mutations (unavailability edits,
    // supervisor changes) reshape the dashboard health/hotspots.
    invalidateAdminCoverage();
    invalidateAdminDashboard();
  },
  dueDateChanged: () => {
    invalidateDueDates();
    invalidateAdminStudents();
  },
  schoolChanged: () => {
    invalidateSchools();
    invalidateAdminSchools();
    invalidateAdminStudents();
    invalidateAdminStudentsSummary();
    invalidateAdminProviders();
    // FP3-120 — Dashboard campus tile updates when a school edits.
    invalidateAdminDashboard();
  },
};

// ── Prefetch — call after admin login/dashboard mount ──────────
/**
 * Warms the "likely next page" datasets during idle time. Called
 * from AdminDashboard on mount. Uses `prefetchQuery` (only fires
 * if not already fresh) and applies the same scope key.
 */
export async function prefetchCommonAdminDatasets(auth) {
  const scope = authScopeKey(auth);
  // Fire independently — no await, they warm in parallel.
  queryClient.prefetchQuery({
    queryKey: adminStudentsKey(scope, undefined),
    queryFn: async () => (await api.getAdminStudents()).data,
    staleTime: STALE.STUDENTS_DIRECTORY,
  });
  queryClient.prefetchQuery({
    queryKey: adminProvidersKey(scope, undefined),
    queryFn: async () => (await api.getAdminProviders()).data,
    staleTime: STALE.PROVIDERS,
  });
  queryClient.prefetchQuery({
    queryKey: dueDatesKey(scope, undefined),
    queryFn: async () => (await api.getDueDates()).data,
    staleTime: STALE.DUE_DATES,
  });
  queryClient.prefetchQuery({
    queryKey: adminProgressKey(scope, undefined),
    queryFn: async () => (await api.getAdminProgress()).data,
    staleTime: STALE.PROGRESS,
  });
}
